import {
  Alert,
  InteractiveContainer,
  ListBoxItem,
  PlainButton,
  Radio,
  Tooltip,
} from "@jugl-web/ui-components";
import { ResourcePickerPopover } from "@jugl-web/ui-components/web/ResourcePickerPopover";
import {
  cx,
  useAutoResizedTextarea,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import {
  ChangeEvent,
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AgentConfigData,
  AgentInstructionOptionId,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import {
  DEFAULT_AGENT_CONFIG,
  DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION,
} from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import Lottie from "react-lottie";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as ExpandIcon } from "./assets/expand.svg";
import { ReactComponent as ResetIcon } from "./assets/reset.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import brainAnimation from "./assets/brain.json";
import {
  buildInstructionsWithSelectedFirst,
  CUSTOM_PROMPT_ID,
  normalizeInstructionItems,
} from "./utils";

export const AgentInstructionsPanel: FC<{
  variant?: "default" | "drawer";
  entityId: string;
  onRequestFullscreen?: () => void;
}> = ({ variant = "default", entityId, onRequestFullscreen }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { data } = integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const [createAgentConfig] = integrationsApi.useCreateAgentConfigMutation();

  const isDrawer = variant === "drawer";
  const [businessName, setBusinessName] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const businessNameTextareaRef = useRef<HTMLTextAreaElement>(null);
  const instructionsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editorFieldsRef = useRef<HTMLDivElement>(null);

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [selectedPromptOptionId, setSelectedPromptOptionId] =
    useState<AgentInstructionOptionId>("custom-prompt");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isBusinessNameFocused, setIsBusinessNameFocused] = useState(false);

  const isBusinessNameEmpty = !businessName.trim();

  const showSomethingWentWrongToast = useCallback(() => {
    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  }, [t, toast]);

  const upsertAgentConfig = useCallback(
    async (configPatch: Partial<AgentConfigData>) => {
      if (!data) {
        await createAgentConfig({
          entityId,
          config: {
            ...DEFAULT_AGENT_CONFIG,
            business_name: businessName.trim(),
            ...configPatch,
          },
        }).unwrap();
        return;
      }

      await updateAgentConfig({
        entityId,
        config: {
          ...data.config,
          ...configPatch,
        },
      }).unwrap();
    },
    [businessName, createAgentConfig, data, entityId, updateAgentConfig]
  );

  useEffect(() => {
    const normalizedInstructions = normalizeInstructionItems(
      data?.config.instructions
    );
    const selectedInstruction = normalizedInstructions[0] ?? {
      name: CUSTOM_PROMPT_ID,
      value: "",
    };

    setBusinessName(data?.config.business_name || "");
    setSelectedPromptOptionId(selectedInstruction.name);
    setInstructions(selectedInstruction.value || "");
  }, [data]);

  const persistAgentConfig = useCallback(async () => {
    const trimmedBusinessName = businessName.trim();
    const trimmedInstructions = instructions.trim();

    setBusinessName(trimmedBusinessName);
    setInstructions(trimmedInstructions);

    const nextInstructions = buildInstructionsWithSelectedFirst(
      data?.config.instructions,
      selectedPromptOptionId,
      trimmedInstructions
    );

    const isDirty =
      trimmedBusinessName !== data?.config.business_name ||
      JSON.stringify(nextInstructions) !==
        JSON.stringify(data?.config.instructions);

    if (!isDirty) return;

    try {
      await upsertAgentConfig({
        business_name: trimmedBusinessName,
        instructions: nextInstructions,
      });
    } catch {
      showSomethingWentWrongToast();
      setTimeout(() => {
        setBusinessName(trimmedBusinessName);
        setInstructions(trimmedInstructions);
      }, 10);
    }
  }, [
    data,
    businessName,
    instructions,
    selectedPromptOptionId,
    upsertAgentConfig,
    showSomethingWentWrongToast,
  ]);

  const handleEditorBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget;
      if (next instanceof Node && editorFieldsRef.current?.contains(next)) {
        return;
      }

      persistAgentConfig();
    },
    [persistAgentConfig]
  );

  const handleInstructionsChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const scrollContainer = editorFieldsRef.current;
      const previousScrollTop = scrollContainer?.scrollTop ?? 0;

      setInstructions(event.target.value);

      requestAnimationFrame(() => {
        if (!scrollContainer) return;
        scrollContainer.scrollTop = previousScrollTop;
      });
    },
    []
  );

  const { resize: resizeBusinessName } = useAutoResizedTextarea(
    businessNameTextareaRef
  );
  const { resize: resizeInstructions } = useAutoResizedTextarea(
    instructionsTextareaRef
  );

  useEffect(() => {
    resizeBusinessName();
  }, [businessName, resizeBusinessName]);

  useEffect(() => {
    if (isBusinessNameFocused) {
      businessNameTextareaRef.current?.focus();
    }
  }, [isBusinessNameFocused]);

  useEffect(() => {
    resizeInstructions();
  }, [instructions, resizeInstructions]);

  const predefiniedOptions = useMemo(
    (): ListBoxItem<{ title: string; instructions: string }>[] => [
      {
        id: "custom-prompt",
        value: {
          title: t({
            id: "cx-ai-agents-page.custom-prompt",
            defaultMessage: "Custom Prompt",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["custom-prompt"],
        },
      },
      {
        id: "customer-support-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.customer-support-agent",
            defaultMessage: "Customer Support Agent",
          }),
          instructions:
            DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["customer-support-agent"],
        },
      },
      {
        id: "service-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.service-agent",
            defaultMessage: "Service Agent",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["service-agent"],
        },
      },
      {
        id: "sales-agent",
        value: {
          title: t({
            id: "cx-ai-agents-page.sales-agent",
            defaultMessage: "Sales Agent",
          }),
          instructions: DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION["sales-agent"],
        },
      },
    ],
    [t]
  );

  const selectedPromptTitle = useMemo(() => {
    const option = predefiniedOptions.find(
      (item) => item.id === selectedPromptOptionId
    );
    return (
      option?.value.title ??
      t({
        id: "cx-ai-agents-page.custom-prompt",
        defaultMessage: "Custom Prompt",
      })
    );
  }, [predefiniedOptions, selectedPromptOptionId, t]);

  const isSelectedPromptAlreadyDefault =
    instructions.trim() ===
    DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION[selectedPromptOptionId].trim();

  const handlePromptSelect = useCallback(
    async (optionId: AgentInstructionOptionId) => {
      const nextInstructions = buildInstructionsWithSelectedFirst(
        data?.config.instructions,
        optionId
      );
      const selectedInstruction = nextInstructions[0];

      setSelectedPromptOptionId(optionId);
      setInstructions(selectedInstruction.value);

      try {
        await upsertAgentConfig({
          instructions: nextInstructions,
        });
      } catch {
        showSomethingWentWrongToast();
      }
    },
    [data?.config.instructions, showSomethingWentWrongToast, upsertAgentConfig]
  );

  const handleResetPrompt = useCallback(async () => {
    const defaultValue =
      DEFAULT_AGENT_INSTRUCTIONS_BY_OPTION[selectedPromptOptionId];
    const nextInstructions = buildInstructionsWithSelectedFirst(
      data?.config.instructions,
      selectedPromptOptionId,
      defaultValue
    );

    setInstructions(defaultValue);

    try {
      await upsertAgentConfig({
        instructions: nextInstructions,
      });
    } catch {
      showSomethingWentWrongToast();
    }
  }, [
    data,
    selectedPromptOptionId,
    showSomethingWentWrongToast,
    upsertAgentConfig,
  ]);

  const promptPickerTrigger = (
    <ResourcePickerPopover
      items={predefiniedOptions}
      defaultSelectedIds={[selectedPromptOptionId]}
      selectionBehavior={{ mode: "single", canToggle: false }}
      renderLabel={(item) => item.value.title}
      renderEndIcon={(_, __, isSelected) => (
        <Radio readOnly isChecked={isSelected} size="sm" />
      )}
      onSelect={({ item, onClose }) => {
        handlePromptSelect(item.id as AgentInstructionOptionId);
        onClose();
      }}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          ref={triggerRef}
          as={InteractiveContainer}
          className={cx(
            "flex h-7 items-center gap-1 outline-none",
            isDrawer && "pt-0.5"
          )}
        >
          <span className={cx("text-dark-600 text-xs", isOpen && "underline")}>
            {selectedPromptTitle}
          </span>
          <ChevronIcon
            className={cx("transition-transform", isOpen && "rotate-180")}
          />
        </Trigger>
      )}
    />
  );

  return (
    <>
      <div
        className={cx(
          "flex flex-col gap-4 px-8",
          isDrawer && "h-full min-h-0 gap-6 overflow-hidden px-[127px] py-8"
        )}
      >
        {isDrawer ? (
          <div className="flex items-center justify-between gap-3 px-2">
            <div className="flex items-center gap-3">
              <span className="font-secondary text-[20px] font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.instructions-section-title",
                  defaultMessage: "✍️ Instructions",
                })}
              </span>
              {promptPickerTrigger}
            </div>
            {!isSelectedPromptAlreadyDefault && (
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <PlainButton
                    className="border-grey-400 hover:border-grey-600 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid transition-colors"
                    ref={ref}
                    {...props}
                    onClick={() => setIsResetConfirmOpen(true)}
                  >
                    <ResetIcon />
                  </PlainButton>
                )}
              >
                {t({
                  id: "cx-ai-agents-page.reset-to-default-prompt",
                  defaultMessage: "Reset to default prompt",
                })}
              </Tooltip>
            )}
          </div>
        ) : (
          <div>
            <div className="flex h-8 items-center justify-between">
              <span className="font-secondary font-medium text-black">
                {t({
                  id: "cx-ai-agents-page.instructions-section-title",
                  defaultMessage: "✍️ Instructions",
                })}
              </span>
              <div className="flex gap-4">
                {!isSelectedPromptAlreadyDefault &&
                  selectedPromptOptionId !== "custom-prompt" && (
                    <Tooltip
                      renderTrigger={({ props, ref }) => (
                        <PlainButton
                          className="border-grey-400 hover:border-grey-600 flex h-8 w-8 items-center justify-center rounded-lg border border-solid transition-colors"
                          ref={ref}
                          {...props}
                          onClick={() => setIsResetConfirmOpen(true)}
                        >
                          <ResetIcon />
                        </PlainButton>
                      )}
                    >
                      {t({
                        id: "cx-ai-agents-page.reset-to-default-prompt",
                        defaultMessage: "Reset to default prompt",
                      })}
                    </Tooltip>
                  )}
                {onRequestFullscreen && (
                  <Tooltip
                    renderTrigger={({ props, ref }) => (
                      <PlainButton
                        ref={ref}
                        className="border-grey-400 hover:border-grey-600 flex h-8 w-8 items-center justify-center rounded-lg border border-solid transition-colors"
                        {...props}
                        onClick={onRequestFullscreen}
                      >
                        <ExpandIcon />
                      </PlainButton>
                    )}
                  >
                    {t({
                      id: "cx-ai-agents-page.open-in-full-screen",
                      defaultMessage: "Open in full screen",
                    })}
                  </Tooltip>
                )}
              </div>
            </div>
            {promptPickerTrigger}
          </div>
        )}
        <Tooltip
          isOpen={!isDrawer && isTooltipOpen}
          placement="right"
          zIndex={10}
          className="w-[360px] max-w-full rounded-[20px] bg-[#333333] p-0"
          arrowClassName="fill-[#333333]"
          renderTrigger={({ props, ref }) => (
            <div
              className={cx(
                "border-grey-300 focus-within:border-dark-700 overflow-hidden rounded-xl border border-solid transition-colors",
                isDrawer ? "flex min-h-0 flex-1 flex-col" : "h-[360px]"
              )}
              ref={ref}
              {...props}
            >
              <div
                ref={editorFieldsRef}
                className={cx(
                  "jugl__custom-scrollbar flex flex-col gap-3 overflow-y-auto py-5",
                  isDrawer ? "min-h-0 flex-1 px-5" : "h-full"
                )}
                onBlur={handleEditorBlur}
              >
                <div
                  className={cx(
                    "hover:bg-grey-100 focus-within:bg-grey-100 flex shrink-0 cursor-text flex-col gap-1 py-0.5 px-5 transition-colors",
                    isBusinessNameEmpty &&
                      "bg-tertiary/5 hover:bg-tertiary/5 focus-within:bg-tertiary/5"
                  )}
                  onClick={(e) => {
                    if (e.target === businessNameTextareaRef.current) return;
                    if (isBusinessNameEmpty && !isBusinessNameFocused) {
                      setIsBusinessNameFocused(true);
                      return;
                    }
                    businessNameTextareaRef.current?.focus();
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    {isBusinessNameEmpty && (
                      <Tooltip
                        renderTrigger={({
                          props: tooltipProps,
                          ref: tooltipRef,
                        }) => (
                          <InfoIcon
                            ref={tooltipRef}
                            {...tooltipProps}
                            className="cursor-default"
                          />
                        )}
                      >
                        {t({
                          id: "cx-ai-agents-page.business-name-info",
                          defaultMessage:
                            "If the Business Name is not indicated - it might cause the misleading responses by AI Agent. Make sure to set proper name to avoid misunderstanding",
                        })}
                      </Tooltip>
                    )}
                    <span className="text-dark-700 font-secondary shrink-0 text-sm leading-[140%]">
                      {t({
                        id: "cx-ai-agents-page.your-business-name-label",
                        defaultMessage: "Your Business Name:",
                      })}
                    </span>
                    {!isBusinessNameFocused && isBusinessNameEmpty && (
                      <span className="text-dark-700 font-secondary text-sm ">
                        {t({
                          id: "common.enter",
                          defaultMessage: "Enter",
                        })}
                      </span>
                    )}
                  </div>
                  {(isBusinessNameFocused || !isBusinessNameEmpty) && (
                    <textarea
                      ref={businessNameTextareaRef}
                      value={businessName}
                      onFocus={() => setIsBusinessNameFocused(true)}
                      onChange={(e) =>
                        setBusinessName(e.target.value.trimStart())
                      }
                      onBlur={() => {
                        setIsBusinessNameFocused(false);
                      }}
                      rows={1}
                      className="font-secondary w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-sm leading-[140%] text-black outline-none ring-0 focus:ring-0"
                    />
                  )}
                  {isBusinessNameEmpty && (
                    <span className="text-tertiary-700 font-secondary leading-2 py-1 text-xs">
                      {t({
                        id: "cx-ai-agents-page.mandatory-field",
                        defaultMessage: "Mandatory field",
                      })}
                    </span>
                  )}
                </div>
                <div
                  className="hover:bg-grey-100 focus-within:bg-grey-100 flex cursor-text flex-col gap-0.5 py-0.5 px-5 transition-colors"
                  onClick={() => instructionsTextareaRef.current?.focus()}
                >
                  <span className="text-dark-700 font-secondary shrink-0 text-sm leading-[140%]">
                    {t({
                      id: "cx-ai-agents-page.general-instructions-label",
                      defaultMessage: "General Instructions:",
                    })}
                  </span>
                  <textarea
                    ref={instructionsTextareaRef}
                    value={instructions}
                    onChange={handleInstructionsChange}
                    rows={1}
                    className="font-secondary w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-sm leading-[140%] text-black outline-none ring-0 focus:ring-0"
                  />
                </div>
              </div>
            </div>
          )}
        >
          <div className="border-dark-800 flex items-center justify-between gap-2 border-0 border-b border-solid py-[3px] pl-6">
            <span className="font-secondary text-lg font-semibold leading-[140%] text-white">
              {t({
                id: "cx-ai-agents-page.business-intelligence-instructions-title",
                defaultMessage: "Business intelligence Instructions",
              })}
            </span>
            <Lottie
              options={{ animationData: brainAnimation }}
              width={78}
              height={78}
            />
          </div>
          <div className="py-2.5 px-6">
            <span className="font-secondary text-sm leading-[21px] text-white">
              {t({
                id: "cx-ai-agents-page.custom-instructions-info",
                defaultMessage:
                  "We prepared Custom Instructions generated for your Business based on provided Knowledge",
              })}
            </span>
          </div>
          <div className="flex h-10 items-center justify-end">
            <InteractiveContainer
              className="font-secondary mx-8 mb-6 bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text text-sm font-semibold uppercase leading-3 text-transparent"
              onClick={() => setIsTooltipOpen(false)}
            >
              {t({
                id: "common.okay",
                defaultMessage: "Okay",
              })}
            </InteractiveContainer>
          </div>
        </Tooltip>
      </div>
      <Alert
        isOpen={isResetConfirmOpen}
        onRequestClose={() => setIsResetConfirmOpen(false)}
        title={t({
          id: "cx-ai-agents-page.reset-instructions-dialog-title",
          defaultMessage: "Reset to default",
        })}
        content={t(
          {
            id: "cx-ai-agents-page.reset-instructions-dialog-description",
            defaultMessage:
              "Are you sure you want to reset instructions to the default {agentName} prompt?",
          },
          {
            agentName: (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {selectedPromptTitle}
              </span>
            ),
          }
        )}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "common.reset",
              defaultMessage: "Reset",
            }),
            color: "primary",
            iconEnd: <ArrowIcon />,
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              handleResetPrompt();
            },
          },
        ]}
      />
    </>
  );
};
